import { Checkbox } from 'components';
import { EyeClosedIcon, EyeOpenIcon } from 'components/icons';
import { FakePageData, SelectorFakeDataValuePair } from './FakeDataTypes';
import { TestingContext } from './TestingContext';
import { triggerInputChange } from './triggerInputChange';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import styles from './TestingTools.module.scss';

export type TestingToolsFillerProps = {
  fakeData: FakePageData[];
};

export const TestingToolsVisibleStorageKey = 'testingToolsVisible';

const transformParamCaseToSentenceCase = (keyName: string): string => {
  const readableText = keyName.charAt(0).toUpperCase() + keyName.slice(1);
  return readableText.replaceAll('-', ' ');
};

/**
 * This component is used to fill in fake data on the page or display link for testing purposes.
 * It is not intended to be used in production.
 * It will render fixed in the bottom right corner of the page.
 */
export const TestingTools = (props: TestingToolsFillerProps) => {
  const location = useLocation();
  const {
    colaUrl,
    data: { applicationID, clientID },
  } = useContext(TestingContext);
  const [shouldSubmit, setShouldSubmit] = useState(true);

  const { fakeData } = props;

  const [isFillerVisible, setFillerVisibility] = useState(
    !Boolean(localStorage.getItem(TestingToolsVisibleStorageKey)),
  );

  // didMount is used to prevent useEffect `isFillerVisible` toggle execution on the first render
  const didMount = useRef(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    if (isFillerVisible) {
      localStorage.removeItem(TestingToolsVisibleStorageKey);
    } else {
      localStorage.setItem(TestingToolsVisibleStorageKey, 'hidden');
    }
  }, [isFillerVisible]);

  const matchingUrlData = useMemo(
    () =>
      fakeData.find(
        (fakeData) => fakeData.showAlways || fakeData.url === location.pathname,
      ),
    [location, fakeData],
  );

  const actionButtons = useMemo(() => {
    if (!matchingUrlData) return null;

    const entries = Object.entries(matchingUrlData.items);

    const handleActionButtonClick = (
      selectorValuesPairs: SelectorFakeDataValuePair[],
    ) => {
      selectorValuesPairs.forEach(triggerInputChange(shouldSubmit));
    };

    const btns = entries.map((entry) => {
      const [keyName, selectorValuePairs] = entry;

      const hasButton = selectorValuePairs.some(
        (selectorValuePair) => selectorValuePair[1] === 'button',
      );

      return (
        <button
          key={keyName}
          onClick={() => handleActionButtonClick(selectorValuePairs as any)}
          className={classNames(
            styles.actionbutton,
            hasButton && shouldSubmit ? styles['will-submit'] : undefined,
          )}
        >
          {transformParamCaseToSentenceCase(keyName)}
        </button>
      );
    });

    return <>{btns}</>;
  }, [matchingUrlData, shouldSubmit]);

  const testingToolsEmpty = !actionButtons && !clientID && !applicationID;

  if (testingToolsEmpty || import.meta.env.VITE_ENV === 'production')
    return null;

  return (
    <div
      className={classNames(
        styles.wrapper,
        isFillerVisible ? undefined : styles.minimized,
      )}
    >
      {isFillerVisible && (
        <>
          {(clientID || applicationID) && (
            <div className={styles['links']}>
              {clientID && (
                <a
                  target="_blank"
                  href={`${colaUrl}/#client/${clientID}`}
                  rel="noopener"
                >
                  Client
                </a>
              )}
              {applicationID && (
                <a
                  target="_blank"
                  href={`${colaUrl}/#loanapplication/${applicationID}`}
                  rel="noopener"
                >
                  Application
                </a>
              )}
            </div>
          )}
          {actionButtons !== null ? (
            <>
              {actionButtons}
              <div className={styles.checkbox}>
                <Checkbox
                  text="Submit"
                  value={shouldSubmit}
                  onChange={() => setShouldSubmit(!shouldSubmit)}
                  eventCollector={{ dontTrack: true }}
                />
              </div>
            </>
          ) : null}
        </>
      )}
      <div
        className={styles['hide-icon-wrapper']}
        onClick={() => setFillerVisibility(!isFillerVisible)}
      >
        {isFillerVisible ? (
          <EyeOpenIcon className={styles['hide-icon']} />
        ) : (
          <EyeClosedIcon className={styles['hide-icon']} />
        )}
      </div>
    </div>
  );
};
